/**
 * Ball flight, wind, slope roll, settle (HANDOFF §3.2 / §3.4 / §5) — Agent B.
 *
 * PURE: advances ball state given wind + green slope, then settles. NO DOM,
 * NO direct randomness except the injected rng (scatter at spawn).
 *
 * PHASE 1 CONTRACT — Agent A calls `createBall` (at fire time) and `stepBall`
 * (each Tick) from the reducer. Agent B implements both and OWNS THE §5 BUG FIX:
 * a perfect aim+timing shot must land inside the birdie ring; the
 * landing → scatter → push → slope-roll → settle sequence is the suspect.
 * All magnitudes come from FLIGHT / ROLL / SCATTER in ./constants.ts.
 *
 * §5 ROOT CAUSE / FIX (see HANDOFF §5 hypotheses):
 *  - hyp.2 (the actual culprit): a clean strike must add ZERO lateral push. That
 *    is guaranteed upstream by resolveSwing (pushOff === 0) and HERE by only
 *    rotating a non-zero pushOff into the landing offset.
 *  - hyp.3: the clean-strike landing offset is just the small scatter (≤ SCATTER.player
 *    px, mean ≈4px) — well inside the ≤8px budget — so the post-flight displacement
 *    decomposes into (tiny scatter) + (readable slope break).
 *  - hyp.1: the slope roll uses the re-derived ROLL constants (max-slope ≈14px).
 *  - hyp.4: a ball is only ever SETTLED at rest (rollFramesLeft === 0); scoring is
 *    read by the reducer strictly when phase === 'settled', never mid-roll.
 */
import type { Ball, Club, CreateBallArgs, Green, Rng, ScoreResult, Vec2, Wind } from './types.ts';
import { FLIGHT, ROLL, ROUGH_ROLL, SCATTER, ROLL_GREEN_MARGIN, HALF_PI, CARD_MAP, SHOT_RANGE, BOX_VARIANCE, ZONE_SCALE, TREE, boxForPower, carriesWater, clearsTree } from './constants.ts';

export type { CreateBallArgs } from './types.ts';

function vec(x: number, y: number): Vec2 {
  return { x, y };
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Compute the target carry+roll distance for a shot (B-geometry card model — Golf-Clash
 * dial-it-in). Each card flies a FRACTION of its own POWER range: full power (bv = 1) →
 * `range`. The pin sits at fraction `h = pinDist / range`, so a swing timed to bv = h
 * reaches the pin (no auto-calibration; a pin beyond `range` is unreachable). The card's
 * CONTROL recentres the deviation on the hole (perfect timing always reaches the pin, §5)
 * and scales how far a mis-timed swing strays. SPIN then releases an overshoot further
 * (run) or checks it up (stop). `bv` is the locked backswing fraction (params.power).
 */
function shotTargetDistance(origin: Vec2, pin: Vec2, bv: number, club: Club): number {
  const pinDist = dist(origin, pin);
  const box = boxForPower(club.power); // POWER picks the tee
  const range = SHOT_RANGE[box]; // the tee's SET range = reach
  const h = range > 0 ? pinDist / range : 0; // the pin's fraction along the SET range
  // Distance error scale: the TEE's personality (long = easy distance, damped) modulated by
  // the card's CONTROL. The bigger long range would otherwise amplify the same timing error.
  const dv = BOX_VARIANCE[box].distance * CARD_MAP.distanceVar(club.control);
  const effBv = h + (bv - h) * dv; // recentre on the hole (perfect timing reaches the pin, §5)
  let d = Math.max(0, effBv) * range;
  const over = d - pinDist; // SPIN: run releases an overshoot further, stop checks it up
  if (over > 0) d = pinDist + over * CARD_MAP.runMult(club.spin);
  return Math.max(0, d);
}

/**
 * Spawn a `flight`-phase ball from a resolved shot (§3.4). Computes the target
 * carry distance from power + club carry, and folds the §3.4 landing scatter
 * (rng) + lateral pushOff into `landingOffset` (applied once at landing — §5).
 *
 * The landing offset is the ONLY post-flight displacement before the slope break.
 * For a clean strike pushOff === 0 and scatter ≤ SCATTER.player px (§5 hyp.3).
 */
/**
 * Where a curving club (the Fade) would come to ground if it flew the WHOLE shot in
 * the air — the old per-step bend integrated. We bake this into the heading/target so
 * the carry/roll-out split keeps the exact same landing (a shorter carry would
 * otherwise under-bend the curve). Mirrors {@link stepFlight}'s bend + speed, sans
 * wind/roll (deterministic; no rng).
 */
function curvedLandingPoint(origin: Vec2, angle0: number, dist: number, curve: number): Vec2 {
  let x = origin.x;
  let y = origin.y;
  let a = angle0;
  let travelled = 0;
  let guard = 0;
  while (travelled < dist && guard++ < 100000) {
    const remain = dist - travelled;
    const stepd = Math.min(Math.max(FLIGHT.speedFloor, remain * FLIGHT.speedDecay), remain);
    a += curve * FLIGHT.curvePerStep;
    x += Math.cos(a) * stepd;
    y += Math.sin(a) * stepd;
    travelled += stepd;
  }
  return vec(x, y);
}

/** The FIRST situational obstacle a shot meets along its flight line (§3.7 counters). */
interface Fate {
  /** Along-line distance (px) at which the ball is stopped. */
  readonly dist: number;
  /** Where it ends up (the splash point / the trunk face). */
  readonly point: Vec2;
  /** The forced score it earns there. */
  readonly outcome: ScoreResult;
}

/**
 * Resolve the first situational obstacle a shot meets (the offense↔defense RPS, §3.7).
 *
 * Walks the straight flight line origin→(origin + dir·target) against the ATTACKED green's
 * hazards and returns the CLOSEST obstacle the club fails to beat, or null for a clean flight:
 *  - WATER the club cannot CARRY (non-stop spin): it comes down in the pond → splash (`double`).
 *  - TREE the club cannot CLEAR (low/mid straight): it is blocked at the trunk → knockdown
 *    (`bogey`, a scramble — not a free double, per the Club Lab).
 * High flight / a fade clears a tree; a stop club holds water; sand is never a fly-over obstacle.
 *
 * PURE geometry — no rng, so it never perturbs the §5 / §5b draw order; a hazardless green (the
 * gate fixtures) returns null and the pipeline is byte-for-byte unchanged.
 */
function situationalFate(origin: Vec2, angle: number, target: number, club: Club, green: Green): Fate | null {
  if (green.hazards.length === 0) return null;
  const dirX = Math.cos(angle);
  const dirY = Math.sin(angle);
  let best: Fate | null = null;
  for (const hz of green.hazards) {
    let radius: number;
    let outcome: ScoreResult;
    let stopAtEdge: boolean;
    if (hz.type === 'water') {
      if (carriesWater(club.spin)) continue; // a stop/backspin club flies & holds it
      radius = hz.radius;
      outcome = 'double';
      stopAtEdge = false; // splash mid-pond (rests in the water → it sinks)
    } else if (hz.type === 'tree') {
      if (clearsTree(club.flight, club.shape)) continue; // high flies over / a fade bends around
      radius = TREE.blockRadius;
      outcome = 'bogey';
      stopAtEdge = true; // knocked down at the trunk face
    } else {
      continue; // sand catches a RESTING ball only (handled in scoring), not a fly-over
    }
    const ox = hz.pos.x - origin.x;
    const oy = hz.pos.y - origin.y;
    const along = ox * dirX + oy * dirY; // distance of the hazard centre projected onto the line
    if (along <= 0 || along >= target) continue; // behind the tee, or past where the ball rests
    const perp = Math.abs(ox * -dirY + oy * dirX); // signed cross-track → magnitude
    if (perp >= radius) continue; // the aim line misses this hazard
    const half = Math.sqrt(radius * radius - perp * perp);
    const hitDist = stopAtEdge ? Math.max(0, along - half) : along; // trunk near-edge vs pond centre
    if (best === null || hitDist < best.dist) {
      best = { dist: hitDist, point: vec(origin.x + dirX * hitDist, origin.y + dirY * hitDist), outcome };
    }
  }
  return best;
}

export function createBall(args: CreateBallArgs, rng: Rng): Ball {
  const { side, club, origin, params, targetGreen, playoff } = args;

  const carry = shotTargetDistance(origin, targetGreen.pin, params.power, club);

  // The mis-timed-impact pushOff (perpendicular to the aim) bends the WHOLE flight
  // toward an OFF-LINE landing point, so the ball visibly flies left/right of the
  // green instead of going dead straight and teleporting sideways at the end. A
  // clean strike (pushOff === 0, the §5 hyp.2 invariant) flies exactly along the aim.
  let landX = origin.x + Math.cos(params.angle) * carry;
  let landY = origin.y + Math.sin(params.angle) * carry;
  if (params.pushOff !== 0) {
    const perp = params.angle + HALF_PI;
    landX += Math.cos(perp) * params.pushOff;
    landY += Math.sin(perp) * params.pushOff;
  }
  let angle = Math.atan2(landY - origin.y, landX - origin.x);
  let target = Math.hypot(landX - origin.x, landY - origin.y);

  // Bake any in-air curve (the Fade) into the landing point, then fly STRAIGHT to it
  // (curve = 0). This keeps the exact curved landing under the carry/roll-out split,
  // so §5/§5b are unchanged — the per-step bend over a shorter carry would under-curve.
  const curve = CARD_MAP.shapeCurve(club.shape) * (side === 'you' ? 1 : -1);
  if (curve !== 0) {
    const land = curvedLandingPoint(origin, angle, target, curve);
    angle = Math.atan2(land.y - origin.y, land.x - origin.x);
    target = Math.hypot(land.x - origin.x, land.y - origin.y);
  }

  // Situational obstacle on the line (§3.7 RPS): a club that can't carry front water / clear a
  // tree is stopped there with a forced score. PURE geometry (no rng), so the §5/§5b draw order
  // is untouched; on a hazardless green this is a no-op and the flight is unchanged.
  const fate = situationalFate(origin, angle, target, club, targetGreen);
  const flightTarget = fate !== null ? fate.dist : target;
  const forcedOutcome = fate !== null ? fate.outcome : null;

  // Landing scatter: uniform over the disk of radius SCATTER.player (sqrt(rng)
  // spreads by area, not clustered at centre). Small by design — applied once at
  // rest as an imperceptible jump (§5 hyp.3). Drawn in the SAME order as before.
  const scatterAngle = rng() * Math.PI * 2;
  const scatterDist = Math.sqrt(rng()) * SCATTER.player;
  const ox = Math.cos(scatterAngle) * scatterDist;
  const oy = Math.sin(scatterAngle) * scatterDist;

  return {
    side,
    pos: vec(origin.x, origin.y),
    angle,
    curve: 0, // baked into `angle` above; flight no longer bends per-step
    travelled: 0,
    target: flightTarget, // a situational stop shortens the flight to the obstacle
    landingOffset: vec(ox, oy),
    rollVec: vec(0, 0),
    rollFramesLeft: 0,
    rollFramesTotal: ROLL.frames,
    restRemaining: 0,
    phase: 'flight',
    scoreLabel: null,
    playoff,
    zoneScale: ZONE_SCALE[boxForPower(club.power)], // LONG = largest zones, SHORT = smallest
    windFactor: CARD_MAP.windFactor(club.flight), // LOW cuts wind, HIGH gets tossed (§3.7)
    forcedOutcome, // null ⇒ scored where it rests; set ⇒ a splash/knockdown override
    stolen: false, // a gopher steal is stamped at the fire site, not here
  };
}

/** Slope downhill vector for a green: (cos·mag, sin·mag) — the per-unit break (§3.2). */
function slopeVector(green: Green): Vec2 {
  return vec(Math.cos(green.slopeAngle) * green.slopeMag, Math.sin(green.slopeAngle) * green.slopeMag);
}

/**
 * Advance a ball that is still in flight by one fixed step (§3.4). The ball CARRIES
 * `FLIGHT.carryFraction` of the shot in the air (kept fast — speed decays over the
 * FULL trajectory), then lands and ROLLS OUT the remainder. At touchdown we fold the
 * whole ground move into `rollVec` = landing scatter + forward roll-out + slope break,
 * applied eased over the rolling phase. The FINAL rest is unchanged vs. flying the
 * whole way (so §5/§5b are preserved); only the air-vs-ground split changes.
 */
function stepFlight(ball: Ball, green: Green, wind: Wind): Ball {
  const remain = ball.target - ball.travelled;
  const spd = Math.max(FLIGHT.speedFloor, remain * FLIGHT.speedDecay);
  const carryEnd = ball.target * FLIGHT.carryFraction; // distance the ball stays airborne
  const stepd = Math.min(spd, ball.target - ball.travelled);

  // Curve bends the heading mid-flight (the Fade); applied before the move.
  const angle = ball.curve !== 0 ? ball.angle + ball.curve * FLIGHT.curvePerStep : ball.angle;

  let x = ball.pos.x + Math.cos(angle) * stepd;
  let y = ball.pos.y + Math.sin(angle) * stepd;

  // Wind nudges the ball sideways while it is airborne (§3.7), scaled by the club's FLIGHT:
  // a LOW runner cuts through it (windFactor < 1), a HIGH ball gets tossed (> 1).
  if (wind.remaining > 0) {
    x += wind.accel.x * ball.windFactor;
    y += wind.accel.y * ball.windFactor;
  }

  const travelled = ball.travelled + stepd;

  // Situational knockout (§3.7 RPS): a shot that can't carry front water / clear a tree flies to
  // the obstacle, then STOPS dead — no roll-out (it's in the pond / against the trunk). It is
  // scored by `forcedOutcome` (water splash = double + sink; tree = bogey knockdown).
  if (ball.forcedOutcome !== null) {
    if (travelled >= ball.target) {
      return { ...ball, pos: vec(x, y), angle, travelled: ball.target, phase: 'settled' };
    }
    return { ...ball, pos: vec(x, y), angle, travelled };
  }

  if (travelled < carryEnd) {
    // Still airborne (carry not reached).
    return { ...ball, pos: vec(x, y), angle, travelled };
  }

  // Touchdown at the carry point: roll out the REMAINING distance along the heading,
  // then the slope break (if the projected rest is on/near the green), plus the small
  // landing scatter — all eased over the rolling phase.
  const carry = vec(x, y);
  const rollOut = ball.target - travelled; // ground distance still to cover

  // Surface AT TOUCHDOWN decides the roll-out: a ball that flies onto the GREEN (or its
  // receptive fringe) releases its full roll-out; one that comes down in the ROUGH grabs
  // — it rolls a SHORTER distance and over MORE frames (slower). A perfect shot lands on
  // the green (carryFraction raised for exactly this), so it keeps its full roll (§5).
  const landedOnGreen = dist(carry, green.center) < green.radius + ROUGH_ROLL.greenFringe;
  const rollScale = landedOnGreen ? 1 : ROUGH_ROLL.rollFactor;
  const rollFrames = landedOnGreen ? ROLL.frames : ROUGH_ROLL.frames;

  const projected = vec(
    carry.x + ball.landingOffset.x + Math.cos(angle) * rollOut * rollScale,
    carry.y + ball.landingOffset.y + Math.sin(angle) * rollOut * rollScale,
  );
  const onGreen = dist(projected, green.center) < green.radius + ROLL_GREEN_MARGIN;
  const slope = onGreen ? slopeVector(green) : vec(0, 0);
  const breakWeight = (ROLL.breakFrames + 1) / 2; // M*slopeMag*(breakFrames+1)/2 = §5 break total
  const rollVec = vec(
    ball.landingOffset.x + Math.cos(angle) * rollOut * rollScale + slope.x * ROLL.magnitude * breakWeight,
    ball.landingOffset.y + Math.sin(angle) * rollOut * rollScale + slope.y * ROLL.magnitude * breakWeight,
  );
  return { ...ball, pos: carry, angle, travelled, rollVec, rollFramesLeft: rollFrames, rollFramesTotal: rollFrames, phase: 'rolling' };
}

/**
 * Advance a ball that is rolling by one frame (§3.2). It applies `rollVec` (the
 * forward roll-out + slope break + scatter) eased over `rollFramesTotal` (= N; green
 * landings use ROLL.frames, rough landings the slower ROUGH_ROLL.frames): per-frame
 * weight `2/(N+1) · (framesLeft/N)`, which sums to exactly 1 for ANY N — so the ball
 * always rolls to its precomputed rest and decelerates out, just over more/fewer
 * frames. Settles only when no frames remain (§5 hyp.4 — scoring reads at rest).
 */
function stepRoll(ball: Ball): Ball {
  const n = ball.rollFramesTotal || ROLL.frames; // total frames for THIS roll (green vs rough)
  const t = ball.rollFramesLeft / n; // eases out as frames decrement
  const w = (2 / (n + 1)) * t; // fraction of rollVec applied this frame (Σ over n frames = 1)
  const x = ball.pos.x + ball.rollVec.x * w;
  const y = ball.pos.y + ball.rollVec.y * w;
  const framesLeft = ball.rollFramesLeft - 1;

  if (framesLeft <= 0) {
    return { ...ball, pos: vec(x, y), rollFramesLeft: 0, phase: 'settled' };
  }
  return { ...ball, pos: vec(x, y), rollFramesLeft: framesLeft };
}

/**
 * Advance a single ball by one fixed timestep (§3.2 / §3.4 / §5). Integrates
 * flight + wind, applies the landing offset and begins the slope roll, then
 * settles at rest. A `settled` ball is returned unchanged; the reducer reads its
 * `pos` for scoring exactly once it is settled.
 */
export function stepBall(ball: Ball, green: Green, wind: Wind, _rng: Rng): Ball {
  switch (ball.phase) {
    case 'flight':
      return stepFlight(ball, green, wind);
    case 'rolling':
      return stepRoll(ball);
    case 'settled':
    default:
      return ball;
  }
}
